import { useMutation } from '@apollo/client'
import { formatToDateTime } from 'brazilian-values'
import { Formik } from 'formik'

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  Box,
  Flex,
  FloraButton,
  FloraTypography,
  ModalHeader,
  ModalBody,
  ModalFooter
} from '@grupoboticario/flora-react'

import FormField from '@/components/FormField/FormField'
import ActionModal from '@/components/Modal/ActionModal'
import PageTitle from '@/components/PageTitle/PageTitle'
import ContactsTable from '@/components/Table/ContactsTable/ContactsTable'
import { STATUS } from '@/constants/status'
import { useHeaderContext } from '@/context/header'
import { useUserContext } from '@/context/user'
import PATCH_MEMBER from '@/graphql/mutation/member'
import PATCH_MEMBER_ANONYMIZATION from '@/graphql/mutation/memberAnonymization'
import { MinusCircle } from '@/icons/MinusCircle'
import { Actions } from '@/utils/dictionaries/actionsDictionary/actions'
import { formatDocumentNumber } from '@/utils/format/documentNumber'
import triggerToastSuccess from '@/utils/triggerToastSuccess/triggerToastSuccess'
import { deactivateInput } from '@/utils/validators/deactivateUserInputs'
import { userSchema } from '@/utils/validators/userSchema'

import Shipping from './components/Shipping'
import UserInfos from './components/UserInfos'
import * as S from './RegisterData.styles'

const RegisterData = ({ documentNumber }) => {
  const navigate = useNavigate()
  const { hasPermission } = useUserContext()
  const { member, setMemberStatus, memberStatus } = useHeaderContext()
  const [address, setAddress] = useState({})
  const [modalAnonymization, setModalAnonymization] = useState(false)
  const [initialValues, setInitialValues] = useState(null)
  const [updateMember, { loading: loadingMember }] = useMutation(PATCH_MEMBER, {
    onError: () => {}
  })
  const [anonymizationMember, { loading: loadingAnonymization }] = useMutation(PATCH_MEMBER_ANONYMIZATION, {
    onError: () => {}
  })

  const handleSubmit = (event, values) => {
    event.preventDefault()

    if (values) {
      const bodyRequestAddress = values.postalCode
        ? {
            streetAddress: values.streetAddress,
            addressNumber: values.addressNumber,
            district: values.district,
            complement: values.complement,
            postalCode: values.postalCode.replaceAll('-', ''),
            addressLocality: values.addressLocality,
            addressRegion: values.addressRegion
          }
        : undefined

      const bodyRequestValues = {
        address: bodyRequestAddress,
        birthDate: values.birthDate,
        documentNumber,
        documentType: 'CPF',
        email: values.email,
        familyName: values.familyName,
        gender: values.gender,
        givenName: values.givenName,
        nationality: values.nationality,
        nickName: values.nickName,
        origin: {
          originId: member?.origin?.originId || 'backoffice-fidelidade',
          channel: member?.origin?.channel || 'crm',
          identifier: member?.origin?.identifier || member.name
        },
        whatsapp: values.whatsapp
      }

      return handleSubmitMember(bodyRequestValues)
    }
  }

  const handleSubmitMember = async (bodyRequest) => {
    const { error, errors } = await updateMember({
      variables: {
        documentNumber,
        member: bodyRequest
      }
    })
    triggerToastSuccess(
      error || errors,
      'Usuário alterado com sucesso! O processamento das alterações pode levar até 5 minutos para ser concluído, por favor, aguarde.',
      () => {
        navigate('/members?refresh=true')
      }
    )
  }

  const handleAnonymization = async () => {
    const { error, errors } = await anonymizationMember({
      variables: {
        documentNumber
      }
    })

    triggerToastSuccess(error || errors, `Usuário anonimizado com sucesso!`, () => {
      navigate(`/members?refresh=true`)
    })
  }
  useEffect(() => {
    if (member?.documentNumber) {
      setInitialValues({
        birthDate: member?.birthDate || '',
        createdAt: formatToDateTime(new Date(member?.createdAt)),
        documentNumber: formatDocumentNumber(member?.documentNumber || '', member?.documentType),
        familyName: member?.familyName || '',
        gender: member?.gender || '',
        givenName: member?.givenName || '',
        nationality: member?.nationality || '',
        nickName: member?.nickName || '',
        updatedAt: formatToDateTime(new Date(member?.updatedAt)),
        email: member?.email || '',
        whatsapp: member?.whatsapp || '',
        postalCode: member?.address?.postalCode || '',
        streetAddress: member?.address?.streetAddress || '',
        addressNumber: member?.address?.addressNumber || '',
        district: member?.address?.district || '',
        complement: member?.address?.complement || '',
        addressLocality: member?.address?.addressLocality || '',
        addressRegion: member?.address?.addressRegion || '',
        country: member?.address?.country || ''
      })

      const memberStatus = member?.status
      if (memberStatus) {
        setMemberStatus({
          status: memberStatus,
          isBlocked: memberStatus === STATUS.BLOCKED
        })
      }
    }
  }, [member, setMemberStatus])

  useEffect(() => {
    if (address?.postalCode)
      setInitialValues((values) => ({ ...values, ...address, complement: '', addressNumber: '' }))
  }, [address])

  if (!initialValues) return null

  const deactivateInputRegisterPermission = deactivateInput(
    hasPermission(Actions.REGISTER_CONTACT_INFO),
    memberStatus?.status
  )

  const deactivateInputAnonimyzePermission = deactivateInput(hasPermission(Actions.ANONYMIZE), memberStatus?.status)

  return (
    <Formik enableReinitialize initialValues={{ ...initialValues }} validateOnMount validationSchema={userSchema}>
      {({ errors, values, handleChange, isValid }) => (
        <Box css={S.outerBoxCss}>
          <Box css={S.sectionBoxCss}>
            <PageTitle>Dados cadastrais</PageTitle>
            <UserInfos errors={errors} handleChange={handleChange} isDisabled={deactivateInputRegisterPermission} />
          </Box>
          <Box css={S.fieldGroupCss}>
            <PageTitle>Contato</PageTitle>
            <Flex align="flex-start" css={S.contactRowCss} gap="$4" justify="space-between">
              <FormField
                data-testid="emailInput"
                disabled={deactivateInputRegisterPermission}
                invalid={typeof errors?.email === 'string'}
                invalidText={errors.email}
                labelText="Email: "
                name="email"
                required
              />
              <FormField
                data-testid="whatsappInput"
                disabled={deactivateInputRegisterPermission}
                invalid={typeof errors?.whatsapp === 'string'}
                invalidText={errors.whatsapp}
                labelText="Whatsapp: "
                maxLength="19"
                name="whatsapp"
                required
              />
            </Flex>
          </Box>
          <Box css={S.fieldGroupCss}>
            <PageTitle>Telefone</PageTitle>
            <ContactsTable
              contactType="TELEPHONE"
              data={member?.contacts || []}
              isDisabled={deactivateInputRegisterPermission}
            />
          </Box>
          <Box css={S.fieldGroupCss}>
            <PageTitle>E-mail</PageTitle>
            <ContactsTable
              contactType="EMAIL"
              data={member?.contacts || []}
              isDisabled={deactivateInputRegisterPermission}
            />
          </Box>
          <Box css={S.fieldGroupCss}>
            <PageTitle>Endereço</PageTitle>
            <Shipping
              address={address}
              errors={errors}
              isDisabled={deactivateInputRegisterPermission}
              setAddress={setAddress}
            />
          </Box>
          <Flex align="center" css={S.actionRowCss} gap="$6" justify="space-between">
            <Box css={S.fullWidthButtonCss}>
              <FloraButton
                disabled={deactivateInputAnonimyzePermission}
                isFull
                isUppercase
                onClick={() => setModalAnonymization(true)}
                hierarchy="secondary"
                has="iconRight"
                icon={<MinusCircle />}
              >
                Anonimizar Cadastro
              </FloraButton>
            </Box>
            <Flex css={S.actionsWrapperCss} gap="$6">
              <FloraButton isUppercase onClick={() => navigate(-1)} hierarchy="secondary">
                Cancelar Alterações
              </FloraButton>
              <FloraButton
                disabled={!isValid || deactivateInputRegisterPermission}
                isLoading={loadingMember}
                isUppercase
                onClick={(event) => handleSubmit(event, values)}
                type="submit"
              >
                Salvar Alterações
              </FloraButton>
            </Flex>
          </Flex>
          <ActionModal onOpenChange={setModalAnonymization} open={modalAnonymization} size="small">
            <ModalHeader>Anonimizar usuário</ModalHeader>
            <ModalBody>
              <FloraTypography fontSize="bodyLargeStandard" fontWeight="bold">
                Tem certeza que deseja anonimizar o usuário: {member.username}?
              </FloraTypography>
            </ModalBody>
            <ModalFooter>
              <FloraButton
                data-testid="confirmDelete"
                isLoading={loadingAnonymization}
                onClick={() => handleAnonymization()}
                styleSemantic="error"
              >
                Anonimizar
              </FloraButton>
            </ModalFooter>
          </ActionModal>
        </Box>
      )}
    </Formik>
  )
}

export default RegisterData
